import { useState, useEffect } from 'react';
import { listPaperLibrary, deletePaperFromLibrary } from '../api';

export default function PaperLibrary({ onAddToWorkspace }) {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLibrary = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listPaperLibrary();
      setPapers(res.data || []);
    } catch {
      setError('Could not load library.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLibrary();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Remove "${title}" from library?`)) return;
    setDeletingId(id);
    try {
      await deletePaperFromLibrary(id);
      setPapers((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError('Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = (paper) => {
    if (onAddToWorkspace) {
      onAddToWorkspace({
        id: paper.paper_id,
        title: paper.title,
        authors: paper.authors,
        abstract: paper.abstract,
        published_date: paper.published_date,
        source: paper.source,
        doi_url: paper.doi_url,
      });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="rounded-lg overflow-hidden" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) fetchLibrary(); }}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-[#fafafa] hover:bg-[#f0f0f0] transition-colors text-sm font-medium text-[#171717]"
      >
        <span className="flex items-center gap-2">
          My Paper Library
          {papers.length > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-[#808080]" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
              {papers.length}
            </span>
          )}
        </span>
        <svg
          className={`w-4 h-4 text-[#808080] transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid #ebebeb' }}>
          {loading && <p className="px-4 py-3 text-sm text-[#808080] text-center">Loading...</p>}
          {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}
          {!loading && !error && papers.length === 0 && (
            <p className="px-4 py-4 text-sm text-[#808080] text-center">
              No saved papers yet. Upload a PDF to save it here.
            </p>
          )}
          {papers.map((paper, i) => (
            <div
              key={paper.id}
              className="px-4 py-3 hover:bg-[#fafafa] transition-colors"
              style={{ borderBottom: i < papers.length - 1 ? '1px solid #ebebeb' : undefined }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#171717] line-clamp-2">{paper.title}</p>
                  <p className="text-xs text-[#808080] mt-0.5">
                    {paper.source_filename && <span className="mr-2">{paper.source_filename}</span>}
                    {formatDate(paper.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {onAddToWorkspace && (
                    <button
                      onClick={() => handleAdd(paper)}
                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-[#171717] text-white hover:bg-[#2e2e2e] transition-colors whitespace-nowrap"
                    >
                      Add to workspace
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(paper.id, paper.title)}
                    disabled={deletingId === paper.id}
                    className="px-2.5 py-1 text-xs font-medium rounded-md text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                  >
                    {deletingId === paper.id ? '...' : 'Remove'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
